/* eslint-disable react-hooks/exhaustive-deps */
import { Button, ButtonPrimary } from '@/components/button'
import { ArrowBack } from '@/components/icons'
import { PasswordInput } from '@/components/input'
import { branchTrackRegistration, eventTracker, identifyUser, setUserDetails } from '@/config/trackingFunctions'
import { ROUTE_DASHBOARD, ROUTE_LOGIN, ROUTE_SIGNUP, ROUTE_HARD_SUBSCRIBE } from '@/constants/Routes'
import { TRACKING_TYPES, PAGE_NAMES } from '@/constants/TrackingConstants'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { authSelector } from '@/pages/auth/selectors'
import { SignupPayload } from '@/types'
import { createSignUpStep2ValidationSchema } from '@/utils/schema/SignUpStep2ValidationSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import { capitalize } from 'lodash'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { signup } from '../authSlice'
import { useRemoteConfig } from '@/hooks/useRemoteConfig'

type FormValues = {
  password: string
  confirmPassword: string
}

/**
 * The server rejects a signup for an address that is already registered with
 * `auth.register.already-exists` ("Email already exists"). There is no machine-readable error code
 * on that response today, so match the message.
 */
const isDuplicateEmailError = (err: any) => /email already exists/i.test(err?.message ?? '')

export const CreatePassword = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()

  const { token } = useAppSelector(authSelector)

  const { getBoolean, isReady } = useRemoteConfig()
  const enableHardPaywallAfterSignup = getBoolean('enableHardPaywallAfterSignup', false)

  const validationSchema = createSignUpStep2ValidationSchema(t)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onTouched',
    resolver: yupResolver(validationSchema),
  })

  const onGoBack = () => navigate(-1)

  // One submit in flight at a time: a second click used to send the same form again, and the
  // server answered the one that arrived second with "link expired" though the first had worked.
  const submittingRef = useRef(false)
  const [submitting, setSubmitting] = useState(false)

  const onSubmit = handleSubmit(async (data) => {
    if (submittingRef.current) return
    submittingRef.current = true
    setSubmitting(true)
    const payload: SignupPayload = {
      fullName: location.state.accData.fullName?.trim() || '',
      marketingOptIn: location.state.accData.marketingOptIn,
      birthdate: location.state.accData.birthdate,
      email: location.state.accData.email,
      gender: location.state.accData.gender,
      password: data.password,
      addressId: location.state.accData.addressId,
      phone: location.state.accData.phone,
      isoCode: location.state.accData.isoCode,
      termsOfService: location.state.accData.termsOfService,
      ambassadorCode: '',
      mediaId: null,
      hand: 'NONE',
      defaultRating: 'DOUBLES',
      singlesRating: '',
      doublesRating: '',
      ...(location.state.accData.userId
        ? {
            userId: location.state.accData.userId,
          }
        : {
            userId: null,
          }),
      // Carried from the email-first landing; the server requires it to create a new account.
      ...(location.state.accData.signupToken && {
        signupToken: location.state.accData.signupToken,
      }),
    }
    try {
      const result = await dispatch(signup(payload)).unwrap()
      const userAge = moment().diff(result.result?.user?.birthdate, 'years')
      identifyUser(result.result?.user?.id)
      setUserDetails({
        Gender: capitalize(result.result?.user?.gender),
        Age: userAge,
        Geolocation: result.result?.user?.addresses?.[0]?.shortAddress,
        'Email ID': result.result?.user?.email,
      })
      if (location.state.isClaim) {
        eventTracker(TRACKING_TYPES.CLAIM_SUCCESSFUL, {
          'Page Name': PAGE_NAMES.SIGN_UP,
          'Sub Page Ref': PAGE_NAMES.SIGN_UP_STEP2,
          'User ID': result?.result?.user?.id,
        })
        branchTrackRegistration(result?.result?.user?.email, 'Account Claim')
      } else {
        eventTracker(TRACKING_TYPES.SIGN_UP_SUCCESS, {
          'Page Name': PAGE_NAMES.SIGN_UP,
          'Sub Page Ref': PAGE_NAMES.SIGN_UP_STEP2,
          Gender: result?.result?.user?.gender,
          Email: result?.result?.user?.email,
          Age: userAge,
          Geolocation: result?.result?.user?.addresses?.[0]?.shortAddress,
        })
        branchTrackRegistration(result?.result?.user?.email, 'Account Signup')
      }
    } catch (err: any) {
      eventTracker(TRACKING_TYPES.SIGN_UP_ERROR, { error: err.message })

      if (err?.status === 409) {
        // The account was created (an earlier submit went through); only signing in is left.
        toast.error(err.message)
        navigate(ROUTE_LOGIN)
        return
      }

      const supportUrl = 'https://dupr.zendesk.com/hc/en-us/requests/new?ticket_form_id=26010354856852'

      if (location?.state?.accData?.userId) {
        const enhancedErrorMessage = (
          <span>
            The provided email does not match the email on record. Please{' '}
            <a
              href={supportUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'underline', color: 'inherit' }}
            >
              submit a support request here
            </a>{' '}
            to claim the account.
          </span>
        )
        toast.error(enhancedErrorMessage)
      } else if (isDuplicateEmailError(err)) {
        // Signup is now where a taken email is discovered — the /exists pre-check on the previous
        // step has been removed. Deliberately does NOT bounce back to the signup step: `accData`
        // cannot faithfully repopulate that form (fullName -> first/last is lossy and the address
        // placeId is not carried), so a redirect would silently discard everything they typed.
        toast.error(
          <span>
            An account already uses this email.{' '}
            <Link to={ROUTE_LOGIN} style={{ textDecoration: 'underline', color: 'inherit' }}>
              Log in instead
            </Link>
            , or go back and use a different address.
          </span>
        )
      } else {
        toast.error(err.message)
      }
    } finally {
      submittingRef.current = false
      setSubmitting(false)
    }
  })

  useEffect(() => {
    if (isReady && token) {
      if (enableHardPaywallAfterSignup) {
        navigate(ROUTE_HARD_SUBSCRIBE, { relative: 'path', replace: true })
      } else {
        navigate(ROUTE_DASHBOARD, { relative: 'path', replace: true })
      }
    }
  }, [token, isReady, enableHardPaywallAfterSignup])

  useEffect(() => {
    if (!location.state) navigate(ROUTE_SIGNUP, { relative: 'path', replace: true })
  }, [location])

  return (
    <div className="w-screen h-screen bg-white flex sm:justify-center sm:items-center">
      <div className="flex flex-col justify-between sm:justify-start pb-14 sm:pb-0 w-full px-4 sm:px-0 sm:w-[390px] gap-y-4">
        <div className="flex relative flex-col sm:flex-row sm:items-center mt-8 sm:mt-0 gap-y-4 pb-0 sm:pb-6">
          <Button onClick={onGoBack} className="hover:opacity-50 flex sm:absolute sm:-left-12">
            <ArrowBack className="text-navy-900" />
          </Button>
          <h1 className="text-2xl xs:text-3xl font-bold text-navy-900">{t('auth.createPassword')}</h1>
        </div>
        <div className="flex flex-col gap-y-4 px-3">
          <PasswordInput
            label={t('common.password')}
            placeholder={t('common.passwordPlaceHolder')}
            labelClassName="!text-navy-900"
            instructs={[t('auth.passInstructLength'), t('auth.passInstructStructure')]}
            iconClassName="!text-navy-900"
            {...register('password')}
            error={errors.password?.message}
            autoFocus
          />
          <PasswordInput
            label={t('common.confirmPassword')}
            placeholder={t('common.confirmPasswordPlaceHolder')}
            labelClassName="!text-navy-900"
            iconClassName="!text-navy-900"
            {...register('confirmPassword')}
            error={errors.confirmPassword?.message}
            onPaste={(e) => e.preventDefault()}
          />
        </div>
        <div className="flex justify-center items-center px-3">
          <ButtonPrimary
            title={t('common.submit')}
            className="w-60 !py-[14px]"
            onClick={onSubmit}
            disabled={submitting}
          />
        </div>
      </div>
    </div>
  )
}
